"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ArrowUpRight, CircleCheck, CircleHelp } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { getAuthUrl } from "@/lib/auth-urls";
import { useLanguage } from "@/lib/LanguageContext";

const YEARLY_DISCOUNT = 20;

const Pricing = () => {
    const [selectedBillingPeriod, setSelectedBillingPeriod] = useState("monthly");
    const t = useTranslations("marketing.pricing");
    const { language } = useLanguage();

    const tooltipContent = {
        ai_agent: t("features.aiAgent"),
        ai_builder: t("features.advancedAI"),
        ai_analytics: t("features.predictiveAnalytics"),
    };

    const plans = [
        {
            name: t("plans.starter.name"),
            price: 0,
            currency: t("currency"),
            description: t("plans.starter.description"),
            features: [
                { title: t("features.standardStorefront") },
                { title: t("features.ecobuilderPage") },
                { title: t("features.ecothemeStore") },
                { title: t("features.products50") },
                { title: t("features.basicAI"), tooltip: tooltipContent.ai_builder },
                { title: t("features.emailSupport") },
                { title: t("features.standardAnalytics") },
            ],
            buttonText: t("plans.starter.button"),
            buttonLink: getAuthUrl(language, '/signup'),
        },
        {
            name: t("plans.professional.name"),
            price: 4490,
            currency: t("currency"),
            isRecommended: true,
            description: t("plans.professional.description"),
            features: [
                { title: t("features.premiumThemes") },
                { title: t("features.unlimitedProducts") },
                { title: t("features.advancedAI"), tooltip: tooltipContent.ai_builder },
                { title: t("features.aiAgent"), tooltip: tooltipContent.ai_agent },
                { title: t("features.smartRouting") },
                { title: t("features.prioritySupport") },
            ],
            isPopular: true,
            buttonText: t("plans.professional.button"),
            buttonLink: getAuthUrl(language, '/signup?plan=pro'),
        },
        {
            name: t("plans.enterprise.name"),
            price: 19900,
            currency: t("currency"),
            description: t("plans.enterprise.description"),
            features: [
                { title: t("features.customStorefront") },
                { title: t("features.unlimitedEverything") },
                { title: t("features.dedicatedManager") },
                { title: t("features.predictiveAnalytics"), tooltip: tooltipContent.ai_analytics },
                { title: t("features.whiteLabel") },
                { title: t("features.dedicatedSupport") },
                { title: t("features.customAPI") },
            ],
            buttonText: t("plans.enterprise.button"),
            buttonLink: `/${language}/contact`,
        },
    ];

    return (
        <section id="pricing" className="flex flex-col items-center justify-center py-12 md:py-20 px-6">
            <h2 className="text-4xl md:text-5xl font-semibold text-center tracking-[-0.03em]">
                {t("title")}
            </h2>
            <p className="mt-3 text-xl text-center text-muted-foreground">
                {t("subtitle")}
            </p>

            <Tabs
                value={selectedBillingPeriod}
                onValueChange={setSelectedBillingPeriod}
                className="mt-8"
            >
                <TabsList className="h-11 bg-background border rounded-full">
                    <TabsTrigger
                        value="monthly"
                        className="px-4 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                        {t("monthly")}
                    </TabsTrigger>
                    <TabsTrigger
                        value="yearly"
                        className="px-4 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                        {t("yearly")} ({t("save")} {YEARLY_DISCOUNT}%)
                    </TabsTrigger>
                </TabsList>
            </Tabs>
            <div className="mt-12 sm:mt-16 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 items-center gap-8 lg:gap-0">
                {plans.map((plan) => (
                    <div
                        key={plan.name}
                        className={cn("relative p-6 bg-background border px-8 rounded-lg flex flex-col", {
                            "shadow-[0px_2px_12px_0px_rgba(0,0,0,0.07)] py-20 z-10 px-10 lg:-mx-2 overflow-hidden":
                                plan.isPopular,
                        })}
                    >
                        {plan.isPopular && (
                            <Badge className="absolute top-0 right-0 rtl:right-auto rtl:left-0 rounded-none px-5 py-1 uppercase rounded-bl-lg rtl:rounded-bl-none rtl:rounded-br-lg">
                                Most Popular
                            </Badge>
                        )}
                        <h3 className="text-lg font-medium">{plan.name}</h3>
                        <p className="mt-2 text-4xl font-semibold">
                            {selectedBillingPeriod === "monthly"
                                ? plan.price.toLocaleString()
                                : Math.round(plan.price * ((100 - YEARLY_DISCOUNT) / 100)).toLocaleString()}
                            <span className="ms-1.5 text-lg text-muted-foreground font-normal">
                                {plan.currency}
                            </span>
                            <span className="ms-1 text-sm text-muted-foreground font-normal">
                                {t("perMonth")}
                            </span>
                        </p>
                        <p className="mt-4 text-sm text-muted-foreground">
                            {plan.description}
                        </p>

                        <Button
                            asChild
                            variant={plan.isPopular ? "default" : "outline"}
                            size="lg"
                            className={`w-full mt-6 rounded-md text-base ${plan.isPopular ? 'from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5' : ''}`}
                        >
                            <a href={plan.buttonLink}>
                                {plan.buttonText} <ArrowUpRight className="w-4 h-4 ms-1" />
                            </a>
                        </Button>
                        <Separator className="my-8" />
                        <ul className="space-y-3">
                            {plan.features.map((feature) => (
                                <li key={feature.title} className="flex items-start gap-1.5">
                                    <CircleCheck className="h-4 w-4 mt-1 text-green-600" />
                                    {feature.title}
                                    {feature.tooltip && (
                                        <Tooltip>
                                            <TooltipTrigger className="cursor-help" aria-label={feature.tooltip}>
                                                <CircleHelp className="h-4 w-4 mt-1 text-gray-500" />
                                            </TooltipTrigger>
                                            <TooltipContent>{feature.tooltip}</TooltipContent>
                                        </Tooltip>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default Pricing;
